import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import enLanding from '../locales/en/landing.json';
import esLanding from '../locales/es/landing.json';
import deLanding from '../locales/de/landing.json';

type Language = 'en' | 'es' | 'de';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: {
        landing: typeof enLanding;
    };
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/** The landing page is the only route with translated content, served at /, /es, /de. */
function localeFromPathname(pathname: string): Language | null {
    const segment = pathname.split('/')[1];
    return segment === 'es' || segment === 'de' ? segment : (pathname === '/' ? 'en' : null);
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [language, setLanguageState] = useState<Language>(() => {
        const urlLocale = localeFromPathname(location.pathname);
        if (urlLocale) return urlLocale;
        const saved = localStorage.getItem('app-language');
        return (saved === 'en' || saved === 'es' || saved === 'de') ? (saved as Language) : 'en';
    });

    useEffect(() => {
        localStorage.setItem('app-language', language);
    }, [language]);

    // Keep language in sync with the URL when navigating directly to a localized landing route.
    useEffect(() => {
        const urlLocale = localeFromPathname(location.pathname);
        if (urlLocale && urlLocale !== language) {
            setLanguageState(urlLocale);
        }
    }, [location.pathname]);

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        // Only the landing page has per-language URLs; elsewhere this is just a stored preference.
        if (localeFromPathname(location.pathname) !== null) {
            const target = lang === 'en' ? '/' : `/${lang}`;
            if (target !== location.pathname) navigate(target);
        }
    };

    const getLandingTranslations = () => {
        switch (language) {
            case 'es': return esLanding;
            case 'de': return deLanding;
            default: return enLanding;
        }
    };

    const t = {
        landing: getLandingTranslations() as typeof enLanding,
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
